import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { IconButton } from '@material-ui/core'
import Card from '../../../../../molecules/Card'
import SydButton from '../../../../../commonDesign/Button'
import SydModal from '../../../../../commonDesign/SydModal'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import Icon from '../../../../../atoms/Icon'
import { ICON_NAMES } from '../../../../../../constants'
import { decipherError } from '../../../../../../utils/decipherError'
import { sectionHeaderStyle, getDefinition, useSaveDefinitionSection } from './shared'

function ChangeTrackingModal ({ open, onClose, target, currentlyEnabled }) {
  const saveSection = useSaveDefinitionSection(target)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const willEnable = !currentlyEnabled

  const handleConfirm = useCallback(async () => {
    try {
      setError(null)
      setProcessing(true)
      await saveSection({ changeTracking: willEnable ? {} : null })
      onClose?.()
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [saveSection, willEnable, onClose])

  return (
    <SydModal open={open} onClose={onClose} title={willEnable ? 'Enable Change Tracking' : 'Disable Change Tracking'} size='small'>
      <p style={{ fontSize: '14px', lineHeight: '1.6', margin: '0 0 16px' }}>
        {willEnable
          ? 'Enabling change tracking will add changeHash and lastChangeDate columns to this table on the next migration check. This is required for virtual associations.'
          : 'Disabling change tracking will remove the changeHash and lastChangeDate columns from this table on the next migration check. Any existing virtual associations will stop working.'}
      </p>
      <SydModalActions>
        {error ? <div style={{ color: 'var(--color-danger-500)', fontSize: '14px', marginRight: 'auto' }}>{error}</div> : null}
        <SydButton disabled={processing} variant='ghost' size='md' onClick={onClose}>Cancel</SydButton>
        <SydButton
          processing={processing}
          variant='primary'
          priority={willEnable ? 'main' : 'warning'}
          size='md'
          onClick={handleConfirm}
        >
          {willEnable ? 'Enable' : 'Disable'}
        </SydButton>
      </SydModalActions>
    </SydModal>
  )
}

ChangeTrackingModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  target: PropTypes.object,
  currentlyEnabled: PropTypes.bool
}

export default function ChangeTrackingCard ({ target }) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const definition = getDefinition(target)
  const enabled = !!definition.changeTracking

  return (
    <Card>
      <div style={sectionHeaderStyle}>
        <h3>Change Tracking</h3>
        <IconButton size='small' onClick={() => setDialogOpen(true)} title='Change tracking settings'>
          <Icon name={ICON_NAMES.edit} customSize='18px' />
        </IconButton>
      </div>
      <span style={{ fontSize: '14px', color: enabled ? 'var(--color-success-600)' : 'var(--color-neutral-600)' }}>
        {enabled ? 'Enabled' : 'Disabled'}
      </span>
      <ChangeTrackingModal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        target={target}
        currentlyEnabled={enabled}
      />
    </Card>
  )
}

ChangeTrackingCard.propTypes = { target: PropTypes.object.isRequired }
